import { Clock, SealCheck, Tag, User, Lock } from "@phosphor-icons/react"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import { Divider } from "@tokenterminal/core/ui/layout/divider/Divider"
import { calculateTimeAgoValue } from "@tokenterminal/ui/utils/date"
import { ClientOnlySuspense } from "components/ClientOnlySuspense"
import DashboardImage from "modules/studio/components/DashboardImage"
import Link from "next/link"
import { memo, useMemo } from "react"
import { type DashboardWithTag } from "../views/types"

function getDateAsNumber(dateString: string) {
  return new Date(dateString).getTime()
}

const LoginRequiredOverlay = () => (
  <div className="absolute w-full h-full left-0 top-0">
    <div
      className="flex flex-col justify-center items-center w-full h-full absolute"
      style={{ zIndex: 10 }}
    >
      <div
        className="absolute flex gap-2 bg-primary rounded-full px-3 py-2 items-center"
        style={{ bottom: 8, right: 8 }}
      >
        <Lock size={14} />
        <span className="text-xs">Login required</span>
      </div>
    </div>
    <div className="absolute w-full h-full left-0 top-0 rounded-[2.5x] blur-xl bg-bg-default/70 dark:bg-bg-default/70" />
  </div>
)

function Item({
  dashboard,
  isLoggedIn,
  hideCreatedBy,
}: {
  dashboard: DashboardWithTag
  isLoggedIn: boolean
  hideCreatedBy?: boolean
}) {
  const tags = useMemo(() => dashboard.tags.map((tag) => tag.name), [dashboard])
  const mustBeLoggedIn = !isLoggedIn && dashboard.login_required

  const Wrapper = (props: any) =>
    mustBeLoggedIn ? (
      <div {...props} />
    ) : (
      <Link
        href={`/studio/dashboards/${dashboard.user_dashboard_id}`}
        {...props}
      />
    )

  return (
    <Wrapper>
      <Card className="flex flex-col justify-between relative rounded-lg p-4 h-full">
        {mustBeLoggedIn && <LoginRequiredOverlay />}
        <div className="flex flex-col flex-grow gap-5">
          <div className="flex flex-row items-center justify-between">
            <DashboardImage
              logoURL={dashboard.logo_url}
              imageAlt={dashboard.name || "logo"}
              size={24}
            />
            {!hideCreatedBy && (
              <Link href={`/profile/${dashboard.organization_id}`}>
                <div className="flex flex-row gap-1.5 items-center flex-shrink-0">
                  <Icon as={User} width={12} className="text-fg-secondary" />
                  <div className="flex flex-row items-center gap-1.5">
                    <span className="text-xs font-normal text-fg-secondary">
                      {dashboard.created_by}
                    </span>
                    {dashboard.is_official && (
                      <Icon
                        as={SealCheck}
                        width={12}
                        weight="fill"
                        className="text-emerald-500"
                      />
                    )}
                  </div>
                </div>
              </Link>
            )}
          </div>
          <div className="flex flex-col gap-0.5 pb-1">
            <h4 className="text-base mb-1 font-medium leading-tight">
              {dashboard.name}
            </h4>
            <div className="text-sm text-fg-secondary mb-2 line-clamp-2 text-ellipsis">
              {dashboard?.description}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-0 mt-2">
          <Divider className="-mx-4" />
          <div className="flex flex-row gap-4 pt-4 justify-between text-fg-secondary">
            {tags.length > 0 ? (
              <div className="flex flex-row gap-1.5 items-center flex-shrink-0">
                <Icon as={Tag} width={11} className="text-fg-secondary" />
                <span className="text-xs font-normal text-fg-secondary">
                  {tags[0]} {tags.length > 1 && `+ ${tags.length - 1} more`}
                </span>
              </div>
            ) : (
              <div className="flex flex-row gap-1"></div>
            )}
            <div
              className={`flex flex-row gap-1 items-center text-fg-secondary flex-shrink-0 ${mustBeLoggedIn ? "opacity-0" : ""}`}
            >
              <Icon as={Clock} width={12} className="text-fg-secondary" />
              <span className="text-xs font-normal text-fg-secondary">
                <ClientOnlySuspense fallback={null}>
                  {calculateTimeAgoValue(getDateAsNumber(dashboard.updated_at))}
                </ClientOnlySuspense>
              </span>
            </div>
          </div>
        </div>
      </Card>
    </Wrapper>
  )
}

export const DashboardItem = memo(Item)
